import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  ComboboxItem,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { DatePicker, DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCalendar,
  IconChevronLeft,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { addHours } from "date-fns";

import { WithAppointmentsNavbar } from "@layouts/WithAppointmentsNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";
import { TruncatedCell } from "@components/Table/TruncatedCell";

import {
  formatDateToString,
  getIsTimeSlotDisabled,
} from "@utils/dateValidation";
import { getCustomDateRange } from "@utils/getCustomDateRange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";
import { truncateWithEllipsis } from "@utils/truncateText";

import { DEFAULT_PAGINATION } from "@constants/index";

import { useCreateAppointmentSlot } from "@hooks/mutations/appointment/useCreateAppointmentSlot";
import { useDeleteAppointmentSlot } from "@hooks/mutations/appointment/useDeleteAppointmentSlot";
import { useGetAppointmentSlots } from "@hooks/queries/appointment/useGetAppointmentSlots";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useTableColumnCount } from "@hooks/useTableColumnCount";
import { useAuth } from "@contexts/auth";

import { AppointmentSlot } from "@customTypes/appointment";

import { EditAppointmentSlotModal } from "../components/EditAppointmentSlotModal";

import { StyledVirtualSlots } from "./VirtualSlots.styled";


const slotTimes = [8, 10, 12, 14, 16, 18, 20] as const;

export const VirtualSlots = () => {
  const { t, i18n } = useTranslation();
  const { id, fullName, roles } = useAuth();
  const isDoctorView = roles.includes("doctor") && !roles.includes("admin");

  // Using 2 date states to bypass an issue with dateValidation getting invalid date at certain times
  const [date, setDate] = useState<Date | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0,
      0
    );
  });
  const [quantity, setQuantity] = useState<string | number>(1);
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);
  const [doctorFilter, setDoctorFilter] = useState<ComboboxItem | null>(null);
  const [
    editAppointmentSlotModalOpened,
    {
      open: openEditAppointmentSlotModal,
      close: closeEditAppointmentSlotModal,
    },
  ] = useDisclosure(false);
  const [currentSlot, setCurrentSlot] = useState({} as AppointmentSlot);
  const [showAvailableSlots, setShowAvailableSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<AppointmentSlot[]>([]);

  const [filteredDateRange, setFilteredDateRange] =
    useState<[Date | null, Date | null]>();
  const [currentPage, setCurrentPage] = useState(1);
  const { columnCount, tableRef } = useTableColumnCount();

  const {
    mutate: createAppointmentSlot,
    isLoading: isCreateAppointmentSlotLoading,
  } = useCreateAppointmentSlot();
  const {
    data: slotsData,
    isLoading: isSlotsLoading,
    isError: isSlotsError,
    isSuccess: isSlotsSuccess,
  } = useGetAppointmentSlots({
    managementView: true,
    withDoctorAssigned: isDoctorView ? true : doctorFilter?.value
      ? doctorFilter.value === "free"
        ? false
        : true
      : undefined,
    doctorAssigned: isDoctorView ? id :
      doctorFilter?.value === "free" ? "" : doctorFilter?.value ?? "",
    ...(filteredDateRange?.length === 2 &&
      filteredDateRange.every(d => d !== null) &&
      getCustomDateRange(filteredDateRange)),
    pagination: {
      page: currentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
  });

  const { data: availableSlotsData, isLoading: isAvailableSlotsLoading } =
    useGetAppointmentSlots({
      managementView: true,
      doctorAssigned: isDoctorView ? id : doctor?.value ?? "",
      withDoctorAssigned: isDoctorView || !!doctor?.value,
      startDate: selectedDate.toISOString(),
      endDate: new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        23,
        59,
        59
      ).toISOString(),
      pagination: {
        page: 1,
        pageSize: 100,
      },
    });

  const {
    mutate: deleteAppointmentSlot,
    isLoading: isDeleteAppointmentSlotLoading,
  } = useDeleteAppointmentSlot();
  const { data: doctors } = useGetUsers({ role: "doctor", isEnabled: !isDoctorView });

  const doctorsData: ComboboxItem[] | undefined = useMemo(
    () =>
      doctors?.map(doctor => ({ label: doctor.fullName, value: doctor.id })),
    [doctors]
  );

  const handleAddSlot = () => {
    createAppointmentSlot(
      {
        date: date as Date,
        quantity: Number(quantity),
        doctor: isDoctorView ? id : doctor?.value ?? "",
      },
      {
        onSuccess: () => {
          setQuantity(1);
        },
      }
    );
  };

  const rows = useMemo(() => {
    return slotsData?.newData?.map((item, index) => (
      <Table.Tr key={index}>
        <Table.Td>
          {item.doctor?.fullName ? (
            <TruncatedCell
              value={item.doctor?.fullName ?? "N/A"}
              charCount={20}
            />
          ) : (
            <Text fz={14} lh={1.5} c="dimmed">
              N/A
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Stack gap={0}>
            <Text fz={14} lh={1.5}>
              {formatDateToString(item.startTime)}
            </Text>
            <Text fz={13} c="dimmed" lh={1.5}>
              {new Date(item.startTime).toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              -{" "}
              {new Date(item.endTime).toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>{item.quantity}</Table.Td>
        <Table.Td w={"min-content"}>
          <Group gap={3} justify="end">
            <PermissionGate permission="appointments.slots"><ActionIcon
              variant="outline"
              onClick={() => {
                setCurrentSlot(item);
                openEditAppointmentSlotModal();
              }}>
              <IconPencil size={16} stroke={1.5} />
            </ActionIcon></PermissionGate>
            <PermissionGate permission="appointments.slots"><ActionIcon
              variant="outline"
              color="red"
              onClick={() => deleteAppointmentSlot(item.id)}
              loading={isDeleteAppointmentSlotLoading}>
              <IconTrash size={16} stroke={1.5} />
            </ActionIcon></PermissionGate>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [slotsData, i18n.language, isDeleteAppointmentSlotLoading]);

  useEffect(() => {
    // console.log("Date state changed:", {
    //   date: date?.toString(),
    //   isValid: date instanceof Date && !isNaN(date.getTime()),
    // });
    if (date && date.getHours() > 7) {
      handleAddSlot();
    }
  }, [date]);

  useEffect(() => {
    if (selectedDate && availableSlotsData?.newData) {
      import.meta.env.DEV &&
        console.log("[DEV] available slots:", availableSlotsData.newData);
      setAvailableSlots(availableSlotsData.newData);
    } else {
      setAvailableSlots([]);
    }
  }, [selectedDate, availableSlotsData]);

  return (
    <WithSidebar hasBackground hasDashboardNavbar>
      <WithAppointmentsNavbar>
        <StyledVirtualSlots>
          <Stack w={"100%"} gap={"xs"}>
            <Title order={2} fw={500} fz={20}>
              {t("calendar")}
            </Title>
            <Text fz={15}>{t("pick-time-slots")}</Text>
            <Group w={"100%"} gap={"xl"} mt={"1rem"} align="start">
              <DatePicker
                hideOutsideDates
                minDate={new Date()}
                // maxDate={
                //   new Date(new Date().setDate(new Date().getDate() + 21))
                // }
                highlightToday
                value={selectedDate}
                onChange={newDate => {
                  if (newDate) {
                    const cleanDate = new Date(
                      newDate.getFullYear(),
                      newDate.getMonth(),
                      newDate.getDate(),
                      0,
                      0,
                      0,
                      0
                    );
                    setSelectedDate(cleanDate);
                  }
                }}
              />

              <Stack mt={8} flex={"1 0 auto"} maw={260}>
                <Text fz={15}>
                  {selectedDate?.toLocaleString(i18n.language, {
                    dateStyle: "long",
                    // timeStyle: "short",
                  })}
                </Text>

                {showAvailableSlots ? (
                  <Stack>
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      w="fit-content"
                      // px="0"
                      pl="0"
                      leftSection={<IconChevronLeft size={16} />}
                      onClick={() => setShowAvailableSlots(false)}>
                      {t("back")}
                    </Button>
                    <Text fz={14} lh={1.4}>
                      {isDoctorView || doctor?.value
                        ? t("select-time-for-doctor-x", {
                            doctorName: truncateWithEllipsis(
                              isDoctorView ? fullName : doctor?.label ?? "",
                              20
                            ),
                          })
                        : t("select-time-for-free-slot")}
                    </Text>
                    {slotTimes.map(time => (
                      <Button
                        key={time}
                        variant="outline"
                        loading={
                          isCreateAppointmentSlotLoading ||
                          isAvailableSlotsLoading
                        }
                        onClick={() => {
                          if (selectedDate) {
                            setDate(addHours(selectedDate, time));
                          }
                        }}
                        disabled={getIsTimeSlotDisabled(
                          availableSlots,
                          selectedDate,
                          time
                        )}>
                        {i18n.language === "fr"
                          ? `${time}h00`
                          : `${time}:00 ${time >= 12 ? "PM" : "AM"}`}
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Stack gap={"md"} maw={260}>
                    {isDoctorView ? <Text>{fullName}</Text> : <Select
                      label={t("doctor")}
                      placeholder={t("select-doctor")}
                      description={t("proceeding-without-selecting-doctor")}
                      data={doctorsData ?? []}
                      value={doctor?.value ?? null}
                      onChange={(_val, option) => {
                        setQuantity(1);
                        setDoctor(option);
                      }}
                      searchable
                      clearable
                    />}
                    <NumberInput
                      label={t("quantity")}
                      disabled={isDoctorView || !!doctor?.value}
                      description={t("quantity-description")}
                      min={1}
                      max={20}
                      value={quantity}
                      onChange={setQuantity}
                      withAsterisk
                    />
                    <Button
                      mt={"1rem"}
                      disabled={!quantity}
                      onClick={() => {
                        setShowAvailableSlots(true);
                        setSelectedDate(selectedDate);
                      }}>
                      {t("next")}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Group>
          </Stack>

          <Stack gap={"xs"} w={{ base: "100%" }}>
            <Title order={2} fw={500} fz={20}>
              {t("available-slots")}
            </Title>
            <EditAppointmentSlotModal
              slots={slotsData?.newData ?? []}
              slot={currentSlot}
              opened={editAppointmentSlotModalOpened}
              close={closeEditAppointmentSlotModal}
            />
            <Table
              ref={tableRef}
              withRowBorders={true}
              withTableBorder
              highlightOnHover
              highlightOnHoverColor="var(--clr-gray-100)">
              <Table.Thead>
                <Table.Tr bg={"var(--clr-gray-100)"}>
                  <Table.Th>
                    {isDoctorView ? <Text>{fullName}</Text> : <Select
                      searchable
                      clearable
                      placeholder={t("doctor")}
                      // data={filterableDoctors}
                      data={
                        doctorsData
                          ? [
                              { label: "No doctor", value: "free" },
                              ...doctorsData,
                            ]
                          : []
                      }
                      value={doctorFilter?.value ?? null}
                      // value={filteredDoctor?.value ?? null}
                      onChange={(_val, option) => {
                        // setFilteredDoctor(option);
                        setDoctorFilter(option);
                        setCurrentPage(1);
                      }}
                    />}
                  </Table.Th>
                  <Table.Th>
                    {/* <Select
                      searchable
                      maw={{ base: "auto", lg2: 150 }}
                      clearable
                      placeholder={t("date")}
                      data={filterableDates}
                      value={filteredDate?.value ?? null}
                      onChange={(_val, option) => {
                        setFilteredDate(option);
                        setCurrentPage(1);
                      }}
                    /> */}
                    <DatePickerInput
                      type="range"
                      placeholder="Select date range"
                      leftSection={<IconCalendar size={16} stroke={1.5} />}
                      minDate={new Date()}
                      allowSingleDateInRange
                      clearable
                      miw={225}
                      // maw={250}
                      value={filteredDateRange}
                      onChange={val => {
                        setFilteredDateRange(val);
                        setCurrentPage(1);
                      }}
                      // maxDate={new Date()}
                    />
                  </Table.Th>
                  <Table.Th>{t("quantity")}</Table.Th>
                  <Table.Th w={100} ta={"right"}>
                    {t("action")}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {renderLoadingErrorData({
                  isLoading: isSlotsLoading,
                  isError: isSlotsError,
                  isSuccess: isSlotsSuccess,
                  dataLength: slotsData?.newData?.length ?? 0,
                  errorComponent: (
                    <TableErrorRow
                      colSpan={columnCount}
                      message={t("encountered-error")}
                    />
                  ),
                  emptyDataComponent: (
                    <TableEmptyRow
                      colSpan={columnCount}
                      message={t("empty-data")}
                    />
                  ),
                  loadingComponent: (
                    <TableSkeletonRows
                      columns={columnCount}
                      skeletonProps={{
                        widths: ["lg", "md", "md", "sm"],
                        rightAlignLastColumn: true,
                      }}
                    />
                  ),
                  dataComponent: rows,
                })}
              </Table.Tbody>
            </Table>
            <TablePagination
              paginationData={slotsData?.pagination || {}}
              paginatedRows={slotsData?.newData?.length || 0}
              onPageChange={setCurrentPage}
              isLoading={isSlotsLoading}
            />
          </Stack>
        </StyledVirtualSlots>
      </WithAppointmentsNavbar>
    </WithSidebar>
  );
};
